import { useFormik } from 'formik'
import { FC, useEffect, useMemo, useState } from 'react'
import {
  ButtonType,
  initialQueryState,
  OptionsHelper,
  QueryState,
  showAlert,
  showError,
  SituacaoFatura,
  stringifyRequestQuery,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  FormTextArea,
  IOption,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'

import { adicionaItemSchema } from './validation/adicionaItemSchema'
import { adicionarItemFatura } from '../../../faturas-atendimentos-list/core/_requests'
import { AdicionaFaturaAtendimentoDto } from '../../../faturas-atendimentos-list/core/_models'
import { getFaturas } from '../../../faturas-list/core/_requests'
import { ValorAtendimentoDto } from '../../core/_models'
import { useT } from '_metronic/i18n/Metronici18n'

interface IProps {
  idAtendimento: number
  idEmpresa: number
}

export const FinanceiroAtendimentosModal: FC<IProps> = ({ idAtendimento, idEmpresa }) => {
  const { t } = useT()
  const { closeModal, fetchStates } = useModal()

  const { data: valoresAtendimento } = (fetchStates['valoresAtendimento'] ||
    {}) as FetchState<ValorAtendimentoDto>

  const [faturas, setFaturas] = useState<IOption<number>[]>([])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      observacao: '',
      valorProfissional: valoresAtendimento?.valorProfissional || undefined,
      valorEmpresa: valoresAtendimento?.valorEmpresa || undefined,
      fatura: undefined as IOption<number> | undefined,
    },
    validationSchema: useMemo(() => adicionaItemSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const data: AdicionaFaturaAtendimentoDto = {
          observacao: values.observacao.trim() || undefined,
          valorEmpresa: values.valorEmpresa,
          valorProfissional: values.valorProfissional,
          atendimento: idAtendimento,
        }
        await adicionarItemFatura(Number(values.fatura?.value), data)

        showAlert({
          title: 'Sucesso',
          text: `Item adicionado na fatura com sucesso!`,
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const state: QueryState = {
          ...initialQueryState,
          search: `situacao:${SituacaoFatura.Gerada},empresa.id:${idEmpresa}`,
        }
        const resp = await getFaturas(stringifyRequestQuery(state))
        setFaturas(
          OptionsHelper.createOptions(
            resp.data || [],
            ['empresa.nomeFantasia', 'mesAno.mesNome', 'mesAno.ano', 'descricao'],
            'id'
          )
        )
      } catch (error) {
        showError(error)
      }
    }

    fetchData()
  }, [idEmpresa])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        name='fatura'
        entity='fatura'
        options={faturas}
        className='mb-3'
        isRequired
      />
      <div className='row'>
        <div className='col-lg-6 mb-3'>
          <FormInput
            formik={formik}
            name='valorEmpresa'
            type='number'
            entity='valor_empresa'
            isRequired
          />
        </div>
        <div className='col-lg-6 mb-3'>
          <FormInput
            formik={formik}
            name='valorProfissional'
            type='number'
            entity='valor_profissional'
            isRequired
          />
        </div>
      </div>
      <FormTextArea
        name='observacao'
        formik={formik}
        entity='observacao'
        isDisabled={formik.isSubmitting}
      />
      <div className='d-flex justify-content-center mt-10'>
        <Button className='btn-secondary me-3' disabled={formik.isSubmitting} onClick={closeModal}>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
    </form>
  )
}
